/**
 * ZO Bull Algo Trading Platform - Admin Panel
 * Version: 6.2.0
 * 
 * Changes in v6.2.0:
 * - Added admin authentication check on mount
 * - Redirects to /admin/login if not authenticated
 * - Added admin session verification
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AdminPanel.css';
import ResponsiveTable, { Column } from '../components/ResponsiveTable';
import BrandingSettings from '../components/BrandingSettings';
import CMSAdmin from '../components/CMSAdmin';
import AdminReports from '../components/AdminReports';
import SecurityManagement from '../components/SecurityManagement';

interface DashboardStats {
  total_users: number;
  active_users: number;
  new_users_today: number;
  new_users_this_week: number;
  total_subscriptions: number;
  active_subscriptions: number;
  trial_subscriptions: number;
  total_revenue: number;
  monthly_recurring_revenue: number;
}

interface User {
  id: number;
  email: string;
  username: string;
  is_active: boolean;
  subscription_tier: string;
  created_at: string;
}

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [userAnalytics, setUserAnalytics] = useState<{ labels: string[]; datasets: { data: number[] }[] } | null>(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState<{ labels: string[]; datasets: { data: number[] }[] } | null>(null);
  const [brandConfig, setBrandConfig] = useState(null);

  // Check admin authentication
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    
    // Verify token is still valid
    verifyAdminToken(adminToken);
  }, [navigate]);

  const verifyAdminToken = async (token: string) => {
    try {
      const response = await fetch('/api/admin/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        // Token invalid, redirect to login
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        localStorage.removeItem('admin_login_time');
        navigate('/admin/login');
        return;
      }
      
      // Token valid, proceed with fetching data
      fetchDashboardStats();
    } catch (error) {
      console.error('Failed to verify admin token:', error);
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      fetchDashboardStats();
      if (activeTab === 'users') fetchUsers();
      if (activeTab === 'analytics') fetchAnalytics();
    }
  }, [activeTab]);

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch('/api/admin/dashboard/stats', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users?limit=50', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const [usersRes, revenueRes] = await Promise.all([
        fetch('/api/admin/analytics/users?period=30d', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
        }),
        fetch('/api/admin/analytics/revenue?period=30d', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
        })
      ]);
      
      if (usersRes.ok) setUserAnalytics(await usersRes.json());
      if (revenueRes.ok) setRevenueAnalytics(await revenueRes.json());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleSuspendUser = async (userId: number) => {
    if (!confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      const response = await fetch(`/api/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.ok) {
        fetchUsers();
        fetchDashboardStats();
      }
    } catch (error) {
      console.error('Failed to suspend user:', error);
    }
  };

  const handleActivateUser = async (userId: number) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.ok) {
        fetchUsers();
        fetchDashboardStats();
      }
    } catch (error) {
      console.error('Failed to activate user:', error);
    }
  };

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

  const subscriptionData = stats ? [
    { name: 'Active', value: stats.active_subscriptions },
    { name: 'Trial', value: stats.trial_subscriptions },
    { name: 'Cancelled', value: stats.total_subscriptions - stats.active_subscriptions - stats.trial_subscriptions }
  ] : [];

  const renderDashboard = () => (
    <div className="dashboard-content">
      <h2>{t('admin.title')}</h2>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <span className="stat-value">{stats?.total_users || 0}</span>
            <span className="stat-label">{t('admin.totalUsers')}</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats?.active_users || 0}</span>
            <span className="stat-label">{t('admin.activeUsers')}</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <span className="stat-value">₹{(stats?.monthly_recurring_revenue || 0).toLocaleString()}</span>
            <span className="stat-label">{t('admin.monthlyRecurringRevenue')}</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-value">{stats?.total_subscriptions || 0}</span>
            <span className="stat-label">Total Subscriptions</span>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Revenue Overview</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenueAnalytics?.labels?.map((l, i) => ({ date: l, revenue: revenueAnalytics?.datasets?.[0]?.data?.[i] || 0 })) || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Subscription Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={subscriptionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {subscriptionData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => {
    // Define columns for the users table
    const userColumns: Column<User>[] = [
      { key: 'id', header: 'ID', width: '80px', sortable: true },
      { key: 'email', header: 'Email', sortable: true },
      { key: 'username', header: 'Username', sortable: true },
      { 
        key: 'subscription_tier', 
        header: 'Plan',
        render: (value) => (
          <span className={`plan-badge ${value || 'free'}`}>{String(value) || 'Free'}</span>
        )
      },
      { 
        key: 'is_active', 
        header: 'Status',
        render: (value) => (
          <span className={`status-badge ${value ? 'active' : 'inactive'}`}>{value ? 'Active' : 'Suspended'}</span>
        )
      },
      { 
        key: 'created_at', 
        header: 'Joined',
        sortable: true,
        render: (value) => new Date(String(value)).toLocaleDateString()
      },
      {
        key: 'actions',
        header: 'Actions',
        hideOnMobile: true,
        render: (_, row) => (
          row.is_active ? (
            <button className="action-btn suspend" onClick={() => handleSuspendUser(row.id)}>Suspend</button>
          ) : (
            <button className="action-btn activate" onClick={() => handleActivateUser(row.id)}>Activate</button>
          )
        )
      }
    ];

    return (
      <div className="users-content">
        <h2>{t('admin.users')}</h2>
        
        <ResponsiveTable
          columns={userColumns}
          data={users}
          keyField="id"
          emptyMessage="No users found"
          onRowClick={(user) => console.log('User clicked:', user)}
        />
      </div>
    );
  };

  const renderAnalytics = () => (
    <div className="analytics-content">
      <h2>{t('admin.analytics')}</h2>
      
      <div className="charts-grid">
        <div className="chart-card">
          <h3>User Growth (30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userAnalytics?.labels?.map((l, i) => ({ date: l, users: userAnalytics?.datasets?.[0]?.data?.[i] || 0 })) || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="users" stroke="#6366f1" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Revenue (30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueAnalytics?.labels?.map((l, i) => ({ date: l, revenue: revenueAnalytics?.datasets?.[0]?.data?.[i] || 0 })) || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  // Show loading while verifying auth
  if (loading) {
    return (
      <div className="admin-panel-loading">
        <div className="loading-spinner"></div>
        <p>Verifying admin session...</p>
        <style>{`
          .admin-panel-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            background: #1a1a2e;
            color: white;
          }
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #333;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .admin-panel-loading p {
            margin-top: 20px;
            font-size: 1.1rem;
            color: #aaa;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <nav className="admin-nav">
        <div className="nav-header">
          <h3>Admin Panel</h3>
        </div>
        <button className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
          📊 Dashboard
        </button>
        <button className={`nav-item ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          👥 {t('admin.users')}
        </button>
        <button className={`nav-item ${activeTab === 'subscriptions' ? 'active' : ''}`} onClick={() => setActiveTab('subscriptions')}>
          💳 {t('admin.subscriptions')}
        </button>
        <button className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
          📈 {t('admin.analytics')}
        </button>
        <button className={`nav-item ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
          📋 {t('admin.reports')}
        </button>
        <button className={`nav-item ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
          📜 {t('admin.logs')}
        </button>
        <button className={`nav-item ${activeTab === 'branding' ? 'active' : ''}`} onClick={() => setActiveTab('branding')}>
          🎨 {t('admin.branding')}
        </button>
        <button className={`nav-item ${activeTab === 'cms' ? 'active' : ''}`} onClick={() => setActiveTab('cms')}>
          📝 {t('admin.cms')}
        </button>
        <button className={`nav-item ${activeTab === 'security' ? 'active' : ''}`} onClick={() => setActiveTab('security')}>
          🛡️ Security
        </button>
      </nav>

      <main className="admin-content">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'subscriptions' && <div className="placeholder">Subscription management coming soon</div>}
            {activeTab === 'analytics' && renderAnalytics()}
            {activeTab === 'reports' && <AdminReports />}
            {activeTab === 'logs' && <div className="placeholder">Activity logs coming soon</div>}
            {activeTab === 'branding' && (
              <div className="branding-section">
                <h2 className="section-heading">🎨 Branding Settings</h2>
                <p className="section-description">
                  Customize your platform's look and feel. Upload your logo, choose brand colors, and preview changes.
                </p>
                <BrandingSettings 
                  initialConfig={brandConfig}
                  onSave={(config) => {
                    setBrandConfig(config)
                    console.log('Brand config saved:', config)
                  }}
                  onPreview={(config) => {
                    console.log('Previewing:', config)
                  }}
                />
              </div>
            )}
            {activeTab === 'cms' && <CMSAdmin />}
            {activeTab === 'security' && <SecurityManagement />}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;